import { Captions } from 'lucide-react'
import { cn } from '@/lib/cn'
import styles from './CaptionsDock.module.scss'

export interface CaptionsDockProps {
  /** Raw script blob from the video record. We probe it defensively
   * because the schema isn't strictly typed in the response. */
  script: unknown
  durationSec?: number | null
}

interface ScriptSegment {
  text: string
  duration_hint: number | null
}

/**
 * Renders the script as an SRT-like list. We don't have per-word
 * timestamps in the API response (Edge TTS produces them but they aren't
 * persisted in ``project.json``), so we approximate from
 * ``segment.duration_hint``. The result is a coarse "~5s per line" view —
 * good enough to read the script with timing context, not a substitute
 * for the rendered burnt-in caption track.
 */
export function CaptionsDock({ script, durationSec }: CaptionsDockProps) {
  const hook = extractHook(script)
  const segments = extractSegments(script)
  const closing = extractClosing(script)

  if (!hook && segments.length === 0 && !closing) {
    return (
      <div className={styles.empty}>
        Sem roteiro salvo. As legendas geradas pelo pipeline ficam embutidas no MP4 final.
      </div>
    )
  }

  // Walk the script in order and assign cumulative timestamps. Each bloc
  // starts where the previous ended; the hook gets a fixed 2.5s window
  // matching what the renderer uses.
  const blocks: { kind: 'hook' | 'segment' | 'closing'; text: string; start: number; end: number }[] = []
  let cursor = 0

  if (hook) {
    const dur = 2.5
    blocks.push({ kind: 'hook', text: hook, start: cursor, end: cursor + dur })
    cursor += dur
  }
  for (const s of segments) {
    const dur = s.duration_hint ?? 4
    blocks.push({ kind: 'segment', text: s.text, start: cursor, end: cursor + dur })
    cursor += dur
  }
  if (closing) {
    const dur = 2
    blocks.push({ kind: 'closing', text: closing, start: cursor, end: cursor + dur })
    cursor += dur
  }

  return (
    <div className={styles.shell}>
      <div className={styles.toolbar}>
        <span>
          {blocks.length} bloco{blocks.length === 1 ? '' : 's'}
          {durationSec ? ` · ~${formatTime(durationSec)} total` : ''}
        </span>
      </div>
      <div className={styles.list}>
        {blocks.map((b, i) => (
          <div
            key={`${String(i)}-${b.kind}`}
            className={cn(styles.entry, b.kind === 'hook' && styles.entryHook)}
          >
            <span className={styles.idx}>{String(i + 1).padStart(2, '0')}</span>
            <span className={styles.range}>
              {formatTime(b.start)} → {formatTime(b.end)}
            </span>
            <p className={styles.text}>
              {b.kind === 'hook' ? (
                <span className={styles.label}>
                  <Captions aria-hidden style={{ width: 12, height: 12 }} /> Hook
                </span>
              ) : null}
              {b.kind === 'closing' ? (
                <span className={styles.label}>
                  <Captions aria-hidden style={{ width: 12, height: 12 }} /> CTA
                </span>
              ) : null}
              {b.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function extractHook(script: unknown): string {
  if (!script || typeof script !== 'object') return ''
  const s = script as Record<string, unknown>
  return typeof s['hook'] === 'string' ? s['hook'] : ''
}

function extractClosing(script: unknown): string {
  if (!script || typeof script !== 'object') return ''
  const s = script as Record<string, unknown>
  return typeof s['closing'] === 'string' ? s['closing'] : ''
}

function extractSegments(script: unknown): ScriptSegment[] {
  if (!script || typeof script !== 'object') return []
  const s = script as Record<string, unknown>
  const raw = s['segments']
  if (!Array.isArray(raw)) return []
  return raw
    .map((seg) => {
      if (!seg || typeof seg !== 'object') return null
      const o = seg as Record<string, unknown>
      const text = typeof o['text'] === 'string' ? o['text'] : ''
      const duration = typeof o['duration_hint'] === 'number' ? o['duration_hint'] : null
      return { text, duration_hint: duration }
    })
    .filter((s): s is ScriptSegment => s !== null && s.text.length > 0)
}
